import { createAnimation } from '@ionic/vue';

export const instantTransition = (baseEl, opts) => {
  const { enteringEl, leavingEl } = opts;

  const enteringAnimation = createAnimation('entering-page-animation')
    .addElement(enteringEl)
    .duration(0)
    .fromTo('visibility', 'hidden', 'visible');

  const leavingAnimation = createAnimation('leaving-page-animation')
    .addElement(leavingEl)
    .duration(0)
    .afterAddClass('ion-page-hidden')
    .fromTo('visibility', 'visible', 'hidden');

  return createAnimation('root-transition')
    .addAnimation([enteringAnimation, leavingAnimation])
}

export const basicTransition = (baseEl, opts) => {
  const { enteringEl, leavingEl } = opts;

  const ANIMATION_DURATION = 200;

  const enteringAnimation = createAnimation('entering-page-animation')
    .addElement(enteringEl)
    .delay(Math.round(ANIMATION_DURATION / 2))
    .duration(ANIMATION_DURATION)
    .fromTo('opacity', 0, 1);

  const leavingAnimation = createAnimation('leaving-page-animation')
    .addElement(leavingEl)
    .duration(ANIMATION_DURATION)
    .afterAddClass('ion-page-hidden')
    .fromTo('opacity', 1, 0);

  return createAnimation('root-transition')
    .easing('ease-in-out')
    .addAnimation([enteringAnimation, leavingAnimation]);
}

export const basicModalEnterAnimation = (baseEl) => {
  const baseAnimation = createAnimation();
  const backdropAnimation = createAnimation();
  const wrapperAnimation = createAnimation();

  backdropAnimation
    .addElement(baseEl.querySelector('ion-backdrop'))
    .fromTo('opacity', 0.01, 'var(--backdrop-opacity)')
    .beforeStyles({
      'pointer-events': 'none'
    })
    .afterClearStyles(['pointer-events']);

  wrapperAnimation
    .addElement(baseEl.querySelector('.modal-wrapper'))
    .keyframes([
      { offset: 0, opacity: 0.01, transform: 'translateY(0px)' },
      { offset: 1, opacity: 1, transform: 'translateY(0px)' }
    ]);

  return baseAnimation
    .addElement(baseEl)
    .easing('cubic-bezier(0.36,0.66,0.04,1)')
    .duration(280)
    .addAnimation([backdropAnimation, wrapperAnimation]);
}

export const basicModalLeaveAnimation = (baseEl) => {
  const baseAnimation = createAnimation();
  const backdropAnimation = createAnimation();
  const wrapperAnimation = createAnimation();
  const wrapperEl = baseEl.querySelector('.modal-wrapper');

  backdropAnimation
    .addElement(baseEl.querySelector('ion-backdrop'))
    .fromTo('opacity', 'var(--backdrop-opacity)', 0.0);

  wrapperAnimation
    .addElement(wrapperEl)
    .keyframes([
      { offset: 0, opacity: 0.99, transform: 'translateY(0px)' },
      { offset: 1, opacity: 0, transform: 'translateY(0px)' }
    ]);

  return baseAnimation
    .addElement(baseEl)
    .easing('cubic-bezier(0.47,0,0.745,0.715)')
    .duration(200)
    .addAnimation([backdropAnimation, wrapperAnimation]);
}

export const basicContentTransition = (baseEl, opts) => {
  const { enteringEl, leavingEl } = opts;

  //Undefined if not found
  const enteringContentEl = enteringEl.getElementsByTagName("ion-content")[0];
  const leavingContentEl = leavingEl.getElementsByTagName("ion-content")[0];
  const enteringHeaderEl = enteringEl.getElementsByTagName("ion-header")[0];
  const leavingHeaderEl = leavingEl.getElementsByTagName("ion-header")[0];
  //Results always in valid arrays, even if not found
  let enteringImmediateEls = [...enteringEl.getElementsByTagName("ion-fab")];
  let leavingImmediateEls = [...leavingEl.getElementsByTagName("ion-fab")];

  //If it exists, add the header to immediately transition at the beginning
  if (enteringHeaderEl != null) {
    enteringImmediateEls = [enteringHeaderEl, ...enteringImmediateEls];
  }
  if (leavingHeaderEl != null) {
    leavingImmediateEls = [leavingHeaderEl, ...leavingImmediateEls];
  }

  const enteringAnimatedEl = (enteringContentEl != null) ? enteringContentEl : enteringEl;
  const leavingAnimatedEl = (leavingContentEl != null) ? leavingContentEl : leavingEl;

  let animations = [];

  const ANIMATION_DURATION = 200;

  //Add classes to page if it is not the animated one and make it visible throughout the whole animation
  if (enteringEl != enteringAnimatedEl) {
    const enteringPage = createAnimation('entering-page-animation')
      .addElement(enteringEl)
      .delay(Math.round(ANIMATION_DURATION / 2))
      .duration(ANIMATION_DURATION)
      .fromTo('opacity', 1, 1);

    animations.push(enteringPage);
  }

  //Add classes to page if it is not the animated one and make it visible throughout the whole animation until right at the end
  if (leavingEl != leavingAnimatedEl) {
    const leavingPage = createAnimation('leaving-page-animation')
      .addElement(leavingEl)
      .fill('forwards')
      .duration(ANIMATION_DURATION)
      .afterAddClass('ion-page-hidden')
      .keyframes([
            { offset: 0, opacity: 1 },
            { offset: 0.9, opacity: 1 },
            { offset: 1, opacity: 0 },
      ]);

    animations.push(leavingPage);
  }

  const enteringContent = createAnimation('entering-content-animation')
    .addElement(enteringAnimatedEl)
    .delay(Math.round(ANIMATION_DURATION / 2))
    .duration(ANIMATION_DURATION)
    .fromTo('opacity', 0, 1);

  const leavingContent = createAnimation('leaving-content-animation')
    .addElement(leavingAnimatedEl)
    .duration(ANIMATION_DURATION)
    .fromTo('opacity', 1, 0);

  animations = [...animations, enteringContent, leavingContent];

  //If there are elements in the array add the animation
  if (enteringImmediateEls != null && enteringImmediateEls.length > 0) {
    const enteringImmediate = createAnimation('entering-immediate-animation')
      .addElement(enteringImmediateEls)
      .duration(0)
      .fromTo('opacity', 0, 1);
    animations = [enteringImmediate, ...animations];
  }
  if (leavingImmediateEls != null && leavingImmediateEls.length > 0) {
    const leavingImmediate = createAnimation('leaving-immediate-animation')
      .addElement(leavingImmediateEls)
      .duration(0)
      .fromTo('opacity', 1, 0);
    animations = [leavingImmediate, ...animations];
  }

  return createAnimation('root-transition')
    .easing('ease-in-out')
    .addAnimation(animations);
}